import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useLocation, useNavigate } from 'react-router-dom';

import { userSelectors } from 'reducers';
import { signatureDialogSet } from 'reducers/user';
import { notificationDialogOpen } from 'reducers/appNotification';
import { parseFullName } from 'utils/helpers';
import {
    useMatchAtLeastOneRole,
    useSubscriptionCanBeSignedByProvider,
    useUserRoleMatchCategory,
} from 'utils/hooks';
import {
    listingActionButtonStyle,
    roleCategories,
    RoleNames,
    subscriptionStatus,
    userAvailabilityStatus,
} from 'utils/constants';
import {
    useDeleteSubscriptionMutation,
    useExportSubscriptionMutation,
    useGetUserQuery,
    useImpersonateUserMutation,
} from 'api/main';
import { texts } from 'utils/languages/fr';

import {
    ConfirmDialog,
    SubscriptionTransferDialog,
    SMSDialog,
    Loader,
    ActionButton,
} from 'components';
import ProfileSection from './ProfileSection';
import DocumentsSection from './DocumentsSection';
import AlertsSection from './AlertsSection';
import MessagesSection from './MessagesSection';
import { Box, Typography, Breadcrumbs, Link } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import * as icons from 'mdi-material-ui';

const useStyles = makeStyles(theme => ({
    return: {
        lineHeight: 1,
        fontSize: 13,
        color: theme.palette.grey[700],
        flex: '0 1',
        display: 'inline-flex',
        alignItems: 'center',
        cursor: 'pointer',
        textTransform: 'uppercase',
    },
    navIcon: {
        marginRight: theme.spacing(1),
        fontSize: 16,
    },
    action: {
        textTransform: 'uppercase',
        color: theme.palette.primary.main,
        padding: theme.spacing(0, 2),
        lineHeight: 1,
        cursor: 'pointer',

        '&:not(:last-child)': {
            borderRight: `1px solid ${theme.palette.grey[700]}`,
        },
    },
}));

const SubscriptionDetails = () => {
    const { userId, subscriptionId } = useParams();
    const mounted = useRef(false);
    const navigate = useNavigate();

    const userIsNetworkAdmin = useMatchAtLeastOneRole([RoleNames.NETWORK_ADMIN]);
    const userIsStandardServicer = useMatchAtLeastOneRole([RoleNames.SERVICER_STANDARD]);
    const userIsServicer = useUserRoleMatchCategory(roleCategories.SERVICERS);
    const userIsProvider = useUserRoleMatchCategory(roleCategories.PROVIDERS);
    const userIsPromoter = useUserRoleMatchCategory(roleCategories.PROMOTERS);
    const dispatch = useDispatch();
    const classes = useStyles();
    const location = useLocation();
    const isInvestorsPath = location.pathname.includes('investors');

    const internalUser = useSelector(userSelectors.getInternalUser);

    const [impersonateUser, { isLoading: impersonateLoader }] = useImpersonateUserMutation({
        fixedCacheKey: 'user-impersionation',
    });
    const [deleteSubscription, { isSuccess: deleteSuccessful, isLoading: deleteLoader }] =
        useDeleteSubscriptionMutation();
    const {
        data: user,
        isLoading: userLoader,
        isSuccess: userLoaded,
        refetch,
        isError,
    } = useGetUserQuery({ id: userId }, { refetchOnFocus: true });
    const [exportSubscription, { isLoading: exportLoading }] = useExportSubscriptionMutation();

    const subscription = useMemo(
        () => user?.subscriptions?.find(sub => sub.id === +subscriptionId),
        [user, subscriptionId]
    );
    const profile = useMemo(
        () => (subscription?.role?.name?.includes('PP') ? 'PP' : 'PM'),
        [subscription]
    );
    const maxAdmissionFee = useMemo(
        () => subscription?.investmentFund?.maximumAdmissionFee || 0,
        [subscription]
    );

    const [impersonateOpen, setImpersonateOpen] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [smsOpen, setSmsOpen] = useState(false);
    const [subscriptionTransferOpen, setSubscriptionTransferOpen] = useState(false);

    const isSubscriptionFinished = useMemo(
        () => subscription?.subscriptionStatus === subscriptionStatus.FINISHED,
        [subscription]
    );

    const isIndirectSubscription = useMemo(
        () => subscription?.indirectUntransferred,
        [subscription]
    );

    const canSignBS = useSubscriptionCanBeSignedByProvider(subscription);

    const isSubscriptionInProgress = useMemo(
        () => subscription?.subscriptionStatus === subscriptionStatus.IN_PROGRESS,
        [subscription]
    );

    const isAdminDistributor = useMatchAtLeastOneRole([RoleNames.ADMIN_CIF, RoleNames.PSI]);
    const userIsDistributor = useMatchAtLeastOneRole([
        RoleNames.ADMIN_CIF,
        RoleNames.COMMERCIALISATEUR,
        RoleNames.PSI,
    ]);

    const canSubstitute = useMemo(
        () =>
            !internalUser?.impersonatedByUser &&
            (((userIsDistributor || userIsServicer) && isIndirectSubscription) ||
                user?.emailConfirmed ||
                isSubscriptionFinished) &&
            user.availabilityStatus !== userAvailabilityStatus.INACTIVE &&
            !userIsNetworkAdmin &&
            !userIsProvider &&
            !userIsStandardServicer &&
            !userIsPromoter &&
            (userIsDistributor ? !isSubscriptionFinished : true), // distributors can't impersonate finished subscriptions
        [
            isIndirectSubscription,
            userIsDistributor,
            userIsServicer,
            user,
            userIsNetworkAdmin,
            isSubscriptionFinished,
            internalUser,
            userIsProvider,
            userIsPromoter,
            userIsStandardServicer,
        ]
    );

    const canDelete = useMemo(
        () =>
            isSubscriptionInProgress &&
            !userIsNetworkAdmin &&
            !userIsProvider &&
            !userIsPromoter &&
            !userIsStandardServicer,
        [
            isSubscriptionInProgress,
            userIsNetworkAdmin,
            userIsProvider,
            userIsStandardServicer,
            userIsPromoter,
        ]
    );

    const handleTransferDialogOpen = useCallback(() => setSubscriptionTransferOpen(true), []);

    useEffect(() => {
        if ((userLoaded && !user?.subscriptions.find(s => s.id === +subscriptionId)) || isError) {
            navigate(isInvestorsPath ? '/investors' : '/subscriptions');
        }
    }, [userLoaded, user, navigate, subscriptionId, isError, isInvestorsPath]);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    const handleSendMessage = useCallback(() => {
        dispatch(notificationDialogOpen(user));
    }, [dispatch, user]);

    const handleDeleteSubscription = useCallback(() => {
        deleteSubscription({ id: +subscriptionId });
    }, [subscriptionId, deleteSubscription]);

    const handleBreadCrum = useCallback(
        e => {
            e.preventDefault();
            const originalPath = isInvestorsPath ? '/investors' : '/subscriptions';
            navigate(originalPath);
        },
        [isInvestorsPath, navigate]
    );

    const handleImpersonate = useCallback(() => {
        const followupPath =
            userIsDistributor && user?.subscriptions?.length > 1 ? '/new-subscription' : '';
        impersonateUser({ userId: +userId, followupPath });
    }, [userId, impersonateUser, user, userIsDistributor]);

    const handleOpenSignatureChoice = useCallback(
        () => dispatch(signatureDialogSet({ open: true, subscription })),
        [subscription, dispatch]
    );

    const handleCloseSubscriptionTransfer = useCallback(
        error => {
            if (!error) refetch();
        },
        [refetch]
    );

    const handleExport = useCallback(() => {
        exportSubscription(subscription.id);
    }, [exportSubscription, subscription]);

    useEffect(() => {
        if (deleteSuccessful) {
            navigate(isInvestorsPath ? '/investors' : '/subscriptions');
        }
    }, [navigate, deleteSuccessful, isInvestorsPath]);

    return (
        <Loader loading={userLoader || !subscription || !user} marginTop={20}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Breadcrumbs separator={<icons.MenuRight />} aria-label="breadcrumb">
                    <Link underline="hover" variant="h5" href="/" onClick={handleBreadCrum}>
                        {isInvestorsPath
                            ? texts.FINISHED_SUBSCRIPTIONS
                            : texts.ONGOING_SUBSCRIPTIONS}
                    </Link>
                    <Link underline="hover" variant="h5" href="/" onClick={refetch}>
                        {profile === 'PP'
                            ? parseFullName(user)
                            : user?.investorCompany?.corporateName}
                    </Link>
                </Breadcrumbs>
                <ActionButton
                    variant="outlined"
                    sx={listingActionButtonStyle}
                    onClick={handleExport}
                    loading={exportLoading}
                >
                    {texts.EXPORT}
                </ActionButton>
            </Box>
            <ConfirmDialog
                open={impersonateOpen}
                loading={impersonateLoader}
                title={texts.CONNECT_AS('subscriber')}
                handleClose={() => setImpersonateOpen(false)}
                handleConfirm={handleImpersonate}
            >
                {`${texts.VOULOIR_VOUS_CONNECTER} ${parseFullName(user)}\xa0?`}
            </ConfirmDialog>
            <SMSDialog open={smsOpen} handleClose={() => setSmsOpen(false)} receiver={user} />
            <ConfirmDialog
                open={confirmOpen}
                title={texts.SUPPRIMER}
                loading={deleteLoader}
                handleClose={() => setConfirmOpen(false)}
                handleConfirm={handleDeleteSubscription}
            >
                {texts.ETES_VOUS_SURS_DE_VOULOIR_SUPPRIMER}
            </ConfirmDialog>
            <SubscriptionTransferDialog
                open={subscriptionTransferOpen}
                handleClose={() => setSubscriptionTransferOpen(false)}
                closeCallback={handleCloseSubscriptionTransfer}
                maxAdmissionFee={maxAdmissionFee}
                subscriptionId={subscription?.id}
            />
            <ProfileSection
                user={user}
                subscription={subscription}
                setSmsOpen={setSmsOpen}
                onSendMessage={handleSendMessage}
            />
            <DocumentsSection user={user} getData={refetch} subscription={subscription} />
            <AlertsSection getData={refetch} subscription={subscription} user={user} />
            <MessagesSection user={user} />
            <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography
                    onClick={() =>
                        navigate(isSubscriptionInProgress ? '/subscriptions' : '/investors')
                    }
                    className={classes.return}
                >
                    <icons.ArrowLeft className={classes.navIcon} /> {texts.RETOUR}
                </Typography>
                <Box display="flex" alignItems="center">
                    {isAdminDistributor && isIndirectSubscription && (
                        <Typography onClick={handleTransferDialogOpen} className={classes.action}>
                            {texts.TRANSFER_TO_SUBSCRIBER}
                        </Typography>
                    )}
                    {canSignBS && (
                        <Typography onClick={handleOpenSignatureChoice} className={classes.action}>
                            {texts.CHOOSE_SIGNER}
                        </Typography>
                    )}
                    {canSubstitute && (
                        <Typography
                            onClick={() => setImpersonateOpen(true)}
                            className={classes.action}
                        >
                            {userIsDistributor
                                ? texts.CONTINUE_SUBSCRIPTION
                                : texts.IMPERSONATE_SUBSCRIBER}
                        </Typography>
                    )}
                    {!userIsNetworkAdmin &&
                        !userIsProvider &&
                        !userIsPromoter &&
                        !userIsStandardServicer && (
                            <Typography onClick={handleSendMessage} className={classes.action}>
                                {texts.ENVOYER_UN_MESSAGE}
                            </Typography>
                        )}
                    {!userIsNetworkAdmin &&
                        !userIsProvider &&
                        !userIsPromoter &&
                        !userIsStandardServicer &&
                        user?.phoneNumber && (
                            <Typography onClick={() => setSmsOpen(true)} className={classes.action}>
                                {texts.ENVOYER_UN_SMS}
                            </Typography>
                        )}
                    {canDelete && (
                        <Typography onClick={() => setConfirmOpen(true)} className={classes.action}>
                            {texts.SUPPRIMER}
                        </Typography>
                    )}
                </Box>
            </Box>
        </Loader>
    );
};

export default SubscriptionDetails;
